import React, { createContext, useState, useEffect } from 'react';

import CustomerHelper from '../../customers/helpers/CustomerHelper';
import ReceiptHelper from '../helpers/receipt.helper';
import { formatDateDMY } from '../../../../utils/date-time';
import { waStoreIds } from '../constants';

const ReceiptContext = createContext(null);

const customerHelper = new CustomerHelper();
const receiptHelper = new ReceiptHelper();

const ReceiptProvider = props => {
  const [receiptId, setReceiptId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({
    receipt: null,
    verify: null,
  });

  const verifyDataStorageKey = 'VerifyData';
  const receiptDataStorageKey = 'ReceiptData';

  const storeVerifyData = verifyData => {
    sessionStorage.setItem(verifyDataStorageKey, JSON.stringify(verifyData));
  };
  const storeReceiptData = receiptData => {
    sessionStorage.setItem(receiptDataStorageKey, JSON.stringify(receiptData));
  };

  const setVerifyData = verifyData => {
    setData({ ...data, verify: verifyData });
    storeVerifyData(verifyData);
  };

  const setReceiptData = receiptData => {
    setData({ ...data, receipt: receiptData });
    storeReceiptData(receiptData);
  };

  const getVerifySessionData = () =>
    sessionStorage.getItem(verifyDataStorageKey) ? JSON.parse(sessionStorage.getItem(verifyDataStorageKey)) : null;

  const getReceiptSessionData = () =>
    sessionStorage.getItem(receiptDataStorageKey) ? JSON.parse(sessionStorage.getItem(receiptDataStorageKey)) : null;

  const clearSession = () => {
    sessionStorage.removeItem(receiptDataStorageKey);
    sessionStorage.removeItem(verifyDataStorageKey);
  };

  useEffect(() => {
    const updateDataOnReceiptChange = async () => {
      const customerId = window.Jbhifi?.shopifyCustomerId;
      let receiptData = data.receipt?.ReceiptData || data.receipt;
      let receiptCode = receiptData?.Code;
      let receiptResponse = null,
        verifyResponse = null;

      try {
        if (receiptId && (!receiptData || (receiptCode && receiptId !== receiptCode))) {
          if (!isLoading) setIsLoading(true);

          // Fetch session data from previous receipt request
          const sessionReceiptData = getReceiptSessionData();

          if (sessionReceiptData) {
            receiptData = sessionReceiptData || sessionReceiptData?.ReceiptData;
            receiptCode = receiptData?.Code || receiptData?.ReceiptData?.Code;

            if (receiptCode && receiptId === receiptCode) {
              // If session data and receipt codes match, use session data
              receiptResponse = sessionReceiptData;
            } else {
              // Else clear old session data including verify data
              clearSession();
            }
          }

          // If we haven't already found valid session data, request receipt data
          if (!receiptResponse) {
            receiptResponse = await receiptHelper.getReceipt(receiptId);
          }
        }

        // If we have a receipt ID and customer ID, fetch verify data
        if (receiptId && customerId) {
          const sessionVerifyData = getVerifySessionData();

          if (sessionVerifyData) {
            // If session data use session data
            verifyResponse = sessionVerifyData;
          } else {
            // Request verify data
            verifyResponse = await customerHelper.getCustomerData(customerId);
          }
        }
      } catch (error) {
        console.error(error);
        return;
      }

      // Store data and update loading state if receipt data found
      if (receiptResponse) {
        setData({ receipt: receiptResponse, verify: verifyResponse });
        setIsLoading(false);
      }
    };

    updateDataOnReceiptChange();
  }, [receiptId]);

  useEffect(() => {
    if (receiptId && data.receipt) storeReceiptData(data.receipt);
    if (receiptId && data.verify) storeVerifyData(data.verify);
  }, [data]);

  // Values for tracking events
  const trackingData = {
    purchaseAmount: data.receipt?.ReceiptData?.TotalSaleValue ? data.receipt?.ReceiptData?.TotalSaleValue : undefined,
    purchaseDate: data.receipt?.ReceiptData?.SaleDateTime
      ? formatDateDMY(new Date(data.receipt.ReceiptData.SaleDateTime))
      : undefined,
    purchaseStore: data.receipt?.ReceiptData?.LocationDescription
      ? data.receipt.ReceiptData.LocationDescription
      : undefined,
  };

  return (
    <ReceiptContext.Provider
      value={{
        receiptId,
        setReceiptId,
        isLoading,
        isLinked: data.receipt?.ItemLinked ?? data.receipt?.ReceiptData?.ItemLinked,
        data,
        trackingData,
        setVerifyData,
        setReceiptData,
        clearSession,
      }}
      {...props}
    />
  );
};

const useReceiptContext = () => {
  const context = React.useContext(ReceiptContext);
  if (context === undefined || context === null) {
    throw new Error('useReceipt context must be used within a ReceiptProvider');
  }
  return context;
};

export { ReceiptProvider, useReceiptContext };
